import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { Table as MuiTable } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import ArgonBox from 'components/ArgonBox';
import ArgonAvatar from 'components/ArgonAvatar';
import ArgonTypography from 'components/ArgonTypography';
import typography from 'assets/theme/base/typography';
import borders from 'assets/theme/base/borders';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

const StyledTableRow = styled(TableRow)(({ theme, selected }) => ({
  transition: 'background-color 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    '& .MuiTypography-root': {
      color: theme.palette.text.primary,
    },
  },
  ...(selected && {
    backgroundColor: theme.palette.action.selected,
    '& .MuiTypography-root': {
      color: theme.palette.text.primary,
    },
  }),
}));

const StyledArgonTypography = styled(ArgonTypography)(({ theme, selected }) => ({
  transition: 'color 0.3s ease',
  ...(selected && {
    color: theme.palette.text.primary,
  }),
}));

function StageProductTable({ columns, rows }) {
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;
  const theme = useTheme();

  const renderColumns = columns.map(({ name, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columns.length - 1) {
      pl = 3;
      pr = 3;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <ArgonBox
        key={name}
        component="th"
        width={width || 'auto'}
        pt={1.5}
        pb={1.25}
        pl={align === 'left' ? pl : 3}
        pr={align === 'right' ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        color="secondary"
        opacity={0.7}
        sx={({ palette: { light } }) => ({ borderBottom: `${borderWidth[1]} solid ${light.main}` })}
      >
        {name.toUpperCase()}
      </ArgonBox>
    );
  });

  const renderRows = rows?.map((row, key) => {
    const rowKey = `row-${key}`;

    const tableRow = columns?.map(({ name, align }) => {
      let template;

      if (Array.isArray(row[name])) {
        template = (
          <ArgonBox
            key={uuidv4()}
            component="td"
            p={1}
            sx={({ palette: { light } }) => ({
              borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
            })}
          >
            <ArgonBox display="flex" alignItems="center" py={0.5} px={1}>
              <ArgonBox mr={2}>
                <ArgonAvatar src={row[name][0]} name={row[name][1]} variant="rounded" size="sm" />
              </ArgonBox>
              <StyledArgonTypography variant="button" fontWeight="medium" selected={row.selected}>
                {row[name][1]}
              </StyledArgonTypography>
            </ArgonBox>
          </ArgonBox>
        );
      } else {
        template = (
          <ArgonBox
            key={uuidv4()}
            component="td"
            p={1.2}
            textAlign={align}
            lineHeight={0.65}
            sx={({ palette: { light } }) => ({
              borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
            })}
          >
            <StyledArgonTypography
              variant="button"
              fontWeight="regular"
              color="secondary"
              selected={row.selected}
              sx={{
                display: 'inline-block',
                width: 'max-content',
                paddingLeft: '1rem',
                paddingRight: '1rem',
              }}
            >
              {row[name]}
            </StyledArgonTypography>
          </ArgonBox>
        );
      }

      return template;
    });

    return (
      <StyledTableRow key={rowKey} onClick={row.onClick} selected={row.selected}>
        {tableRow}
      </StyledTableRow>
    );
  });

  return useMemo(
    () => (
      <TableContainer>
        <MuiTable>
          <ArgonBox component="thead">
            <TableRow>{renderColumns}</TableRow>
          </ArgonBox>
          <TableBody>{renderRows}</TableBody>
        </MuiTable>
      </TableContainer>
    ),
    [renderColumns, renderRows]
  );
}

// Setting default values for the props of StageProductTable
StageProductTable.defaultProps = {
  columns: [],
  rows: [{}],
};

// Typechecking props for the StageProductTable
StageProductTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
};

export default StageProductTable;
